import * as Schema from "effect/Schema";
import { CloudCatalog } from "./cloudWorkspace.ts";

export const GuestEnvironmentState = Schema.Literals([
  "creating",
  "stopped",
  "starting",
  "running",
  "stopping",
  "deleting",
  "failed",
]);

/** The parent's view of a Guest Environment; its own service remains authoritative for its work. */
export const GuestEnvironment = Schema.Struct({
  id: Schema.String,
  label: Schema.String,
  image: Schema.String,
  size: Schema.String,
  diskGb: Schema.Int,
  state: GuestEnvironmentState,
  revision: Schema.Int,
  networkId: Schema.String,
  machineId: Schema.NullOr(Schema.String),
  enrollmentDigest: Schema.String,
  error: Schema.NullOr(Schema.String),
});
export type GuestEnvironment = typeof GuestEnvironment.Type;

export const GuestEnvironmentList = Schema.Struct({
  revision: Schema.Int,
  guests: Schema.Array(GuestEnvironment),
});
export type GuestEnvironmentList = typeof GuestEnvironmentList.Type;

export const GuestEnvironmentCapabilities = Schema.Struct({
  networkId: Schema.NullOr(Schema.String),
  machineId: Schema.NullOr(Schema.String),
  supported: Schema.Boolean,
  reason: Schema.NullOr(Schema.String),
  catalog: CloudCatalog,
  cpuCount: Schema.Int,
  availableCpuCount: Schema.Int,
  availableMemoryBytes: Schema.Number,
  availableDiskBytes: Schema.Number,
});
export type GuestEnvironmentCapabilities = typeof GuestEnvironmentCapabilities.Type;

export const CreateGuestEnvironment = Schema.Struct({
  requestId: Schema.String,
  expectedRevision: Schema.Int,
  label: Schema.String,
  image: Schema.String,
  size: Schema.String,
  diskGb: Schema.Int,
});
export type CreateGuestEnvironment = typeof CreateGuestEnvironment.Type;

export const GuestEnvironmentAction = Schema.Struct({
  id: Schema.String,
  requestId: Schema.String,
  expectedRevision: Schema.Int,
  action: Schema.Literals(["start", "stop", "delete"]),
});
export type GuestEnvironmentAction = typeof GuestEnvironmentAction.Type;

export class GuestEnvironmentError extends Schema.TaggedErrorClass<GuestEnvironmentError>()(
  "GuestEnvironmentError",
  {
    reason: Schema.Literals([
      "unsupported",
      "not-joined",
      "invalid-request",
      "guest-stopped",
      "operation-pending",
      "revision-conflict",
      "request-conflict",
      "capacity",
      "not-found",
      "helper-unavailable",
      "network-unavailable",
      "operation-failed",
    ]),
    message: Schema.String,
  },
) {}

/** Connect grants provisioning of these exact choices on one parent, without account credentials. */
export const GuestProvisioningRequest = Schema.Struct({
  guestId: Schema.String,
  requestId: Schema.String,
  image: Schema.String,
  size: Schema.String,
  diskGb: Schema.Int,
});
export type GuestProvisioningRequest = typeof GuestProvisioningRequest.Type;

export const GuestProvisioningClaims = Schema.Struct({
  operation: Schema.Literal("guest-provision"),
  memberAccountId: Schema.optional(Schema.String),
  issuer: Schema.String,
  networkId: Schema.String,
  parentMachineId: Schema.String,
  accountId: Schema.String,
  ...GuestProvisioningRequest.fields,
  enrollmentDigest: Schema.String,
  runtimeUrl: Schema.String,
  runtimeSha256: Schema.String,
  runtimeArchitecture: Schema.Literals(["x64", "arm64"]),
  issuedAt: Schema.Number,
  expiresAt: Schema.Number,
});
export type GuestProvisioningClaims = typeof GuestProvisioningClaims.Type;

export const GuestProvisioningGrant = Schema.Struct({
  claims: GuestProvisioningClaims,
  signature: Schema.String,
  enrollmentCode: Schema.String,
  controlUrl: Schema.String,
});
export type GuestProvisioningGrant = typeof GuestProvisioningGrant.Type;

export const GuestActionRequest = Schema.Struct({
  enrollmentDigest: Schema.String,
  guestId: Schema.String,
  requestId: Schema.String,
  action: Schema.Literals(["start", "stop", "delete"]),
  machineId: Schema.NullOr(Schema.String),
});
export type GuestActionRequest = typeof GuestActionRequest.Type;

export const GuestActionClaims = Schema.Struct({
  operation: Schema.Literal("guest-action"),
  enrollmentDigest: Schema.String,
  machineId: Schema.NullOr(Schema.String),
  issuer: Schema.String,
  networkId: Schema.String,
  parentMachineId: Schema.String,
  accountId: Schema.String,
  guestId: Schema.String,
  requestId: Schema.String,
  action: Schema.Literals(["start", "stop", "delete"]),
  issuedAt: Schema.Number,
  expiresAt: Schema.Number,
});
export type GuestActionClaims = typeof GuestActionClaims.Type;
export const GuestActionGrant = Schema.Struct({
  claims: GuestActionClaims,
  signature: Schema.String,
});
export type GuestActionGrant = typeof GuestActionGrant.Type;

/** Connect may wake a bound guest for access, but cannot override an explicit guest stop. */
export const GuestStartForConnectClaims = Schema.Struct({
  operation: Schema.Literal("guest-start-for-connect"),
  issuer: Schema.String,
  networkId: Schema.String,
  parentMachineId: Schema.String,
  accountId: Schema.String,
  guestId: Schema.String,
  machineId: Schema.String,
  requestId: Schema.String,
  issuedAt: Schema.Number,
  expiresAt: Schema.Number,
});
export type GuestStartForConnectClaims = typeof GuestStartForConnectClaims.Type;
export const GuestStartForConnectGrant = Schema.Struct({
  claims: GuestStartForConnectClaims,
  signature: Schema.String,
});
export type GuestStartForConnectGrant = typeof GuestStartForConnectGrant.Type;
